/**
 * Parent Portal API Service
 * 
 * Service for making API calls to the parent portal backend endpoints.
 */

import { apiClient } from './apiClient';

/** Raw shape returned by GET /parent-portal/dashboard/{student_id}. */
interface BackendDashboardResponse {
  student_id: number;
  engagement_summary?: {
    overall_score?: number;
    trend?: string;
    participation_rate?: number;
    learning_style?: string;
    focus_duration?: number;
    weekly_scores?: number[];
  };
  teacher_insights?: Array<{
    teacher_id: number;
    teacher_name: string;
    subject?: string;
    effectiveness_score?: number;
    student_engagement_score?: number;
    compatibility_score?: number;
    key_strengths?: string[];
    classroom_style?: string[];
    experience_years?: number;
    school_ranking_percentile?: number;
  }>;
  recent_alerts?: EarlyWarningResponse[];
  learning_recommendations?: string[];
  upcoming_conferences?: DashboardData['upcoming_conferences'];
  progress_indicators?: Partial<DashboardData['progress_indicators']>;
}

// Types based on the backend API
export interface StudentEngagementSummary {
  student_id: number;
  student_name: string;
  overall_engagement_score: number;
  engagement_percentile: number;
  primary_learning_style: string;
  optimal_conditions: string[];
  recent_trends: Record<string, unknown>;
  focus_duration_minutes: number;
  participation_rate: number;
}

export interface TeacherSummaryForParents {
  teacher_id: number;
  teacher_name: string;
  subject_area: string;
  overall_effectiveness: number;
  student_engagement_score: number;
  compatibility_score?: number;
  teaching_strengths: string[];
  classroom_style: string[];
  experience_years: number;
  school_ranking_percentile: number;
}

export interface EarlyWarningResponse {
  alert_id: number;
  alert_type: string;
  severity: string;
  title: string;
  description: string;
  suggested_actions: string[];
  trigger_date: string;
  status: string;
}

export interface ParentStudentOption {
  id: number;
  name: string;
  grade_level: string;
  district_student_id: string;
  school_name: string;
}

export interface DashboardData {
  student_engagement: {
    current_score: number;
    trend: 'improving' | 'declining' | 'stable';
    participation_rate: number;
    primary_learning_style: string | null;
    focus_duration_minutes: number | null;
    weekly_scores: number[];
  };
  teacher_insights: TeacherSummaryForParents[];
  recent_alerts: EarlyWarningResponse[];
  learning_recommendations: string[];
  upcoming_conferences: Array<{
    conference_id: number;
    teacher_id: number;
    date: string;
    type: string;
    preparation_status: string;
  }>;
  progress_indicators: {
    engagement_trend: string;
    improvement_areas: string[];
    success_factors: string[];
    goals_on_track: boolean;
    next_review_date: string;
  };
}

class ParentPortalApiService {
  // Delegate to the shared apiClient so auth headers, 401 handling, and
  // error shaping stay consistent with the rest of the app.
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return apiClient<T>(`/parent-portal${endpoint}`, options);
  }

  async listLinkedStudents(): Promise<ParentStudentOption[]> {
    return this.makeRequest<ParentStudentOption[]>('/students');
  }

  /**
   * Get comprehensive dashboard data for a parent about their child.
   * The backend response shape differs from the UI model, so map it here.
   */
  async getDashboard(studentId: number, periodDays: number = 30): Promise<DashboardData> {
    const raw = await this.makeRequest<BackendDashboardResponse>(
      `/dashboard/${studentId}?period_days=${periodDays}`
    );

    const engagement = raw.engagement_summary ?? {};
    const progress = raw.progress_indicators ?? {};
    const trend =
      engagement.trend === 'improving' || engagement.trend === 'declining'
        ? engagement.trend
        : 'stable';

    return {
      student_engagement: {
        current_score: engagement.overall_score ?? 0,
        trend,
        participation_rate: engagement.participation_rate ?? 0,
        primary_learning_style: engagement.learning_style ?? null,
        focus_duration_minutes:
          engagement.focus_duration !== undefined ? engagement.focus_duration : null,
        weekly_scores: engagement.weekly_scores ?? [],
      },
      teacher_insights: (raw.teacher_insights ?? []).map((t) => ({
        teacher_id: t.teacher_id,
        teacher_name: t.teacher_name,
        subject_area: t.subject ?? '',
        overall_effectiveness: t.effectiveness_score ?? 0,
        student_engagement_score: t.student_engagement_score ?? 0,
        compatibility_score: t.compatibility_score,
        teaching_strengths: t.key_strengths ?? [],
        classroom_style: t.classroom_style ?? [],
        experience_years: t.experience_years ?? 0,
        school_ranking_percentile: t.school_ranking_percentile ?? 0,
      })),
      recent_alerts: raw.recent_alerts ?? [],
      learning_recommendations: raw.learning_recommendations ?? [],
      upcoming_conferences: raw.upcoming_conferences ?? [],
      progress_indicators: {
        engagement_trend: progress.engagement_trend ?? 'stable',
        improvement_areas: progress.improvement_areas ?? [],
        success_factors: progress.success_factors ?? [],
        goals_on_track: progress.goals_on_track ?? true,
        next_review_date: progress.next_review_date ?? new Date().toISOString(),
      },
    };
  }

  /**
   * Get detailed engagement insights for a specific student
   */
  async getStudentEngagement(studentId: number, periodDays: number = 30): Promise<StudentEngagementSummary> {
    return this.makeRequest<StudentEngagementSummary>(`/student/${studentId}/engagement?period_days=${periodDays}`);
  }

  /**
   * Get summary of all teachers for a specific student
   */
  async getStudentTeachers(studentId: number, includeHistorical: boolean = false): Promise<TeacherSummaryForParents[]> {
    return this.makeRequest<TeacherSummaryForParents[]>(`/student/${studentId}/teachers?include_historical=${includeHistorical}`);
  }

  /**
   * Get early warning alerts for all children of the current parent
   */
  async getAlerts(options: {
    status?: string;
    severity?: string;
    studentId?: number;
  } = {}): Promise<EarlyWarningResponse[]> {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.severity) params.append('severity', options.severity);
    if (options.studentId) params.append('student_id', options.studentId.toString());
    
    const query = params.toString();
    return this.makeRequest<EarlyWarningResponse[]>(`/alerts${query ? `?${query}` : ''}`);
  }

  /**
   * Acknowledge an early warning alert
   */
  async acknowledgeAlert(alertId: number, notes?: string): Promise<{ status: string; alert_id: number }> {
    return this.makeRequest(`/alerts/${alertId}/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  /**
   * Get generated reports for the parent
   */
  async getReports(options: {
    studentId?: number;
    reportType?: string;
    limit?: number;
  } = {}): Promise<Array<Record<string, unknown>>> {
    const params = new URLSearchParams();
    if (options.studentId) params.append('student_id', options.studentId.toString());
    if (options.reportType) params.append('report_type', options.reportType);
    if (options.limit) params.append('limit', options.limit.toString());
    
    const query = params.toString();
    return this.makeRequest<Array<Record<string, unknown>>>(`/reports${query ? `?${query}` : ''}`);
  }

  /**
   * Get public-facing teacher profile with effectiveness data
   */
  async getTeacherProfile(teacherId: number, studentId?: number): Promise<TeacherSummaryForParents> {
    const params = studentId ? `?student_id=${studentId}` : '';
    return this.makeRequest<TeacherSummaryForParents>(`/teacher/${teacherId}/public-profile${params}`);
  }

  /**
   * Get personalized learning optimization recommendations
   */
  async getLearningOptimization(studentId: number): Promise<Record<string, unknown>> {
    return this.makeRequest(`/insights/learning-optimization?student_id=${studentId}`);
  }

  /**
   * Get detailed engagement trends and predictive analytics
   */
  async getEngagementTrends(studentId: number, periodMonths: number = 3, includePredictions: boolean = true): Promise<Record<string, unknown>> {
    return this.makeRequest(`/analytics/engagement-trends?student_id=${studentId}&period_months=${periodMonths}&include_predictions=${includePredictions}`);
  }

  /**
   * Submit parent feedback about teacher effectiveness
   */
  async submitFeedback(studentId: number, teacherId: number, feedbackData: Record<string, unknown>, anonymous: boolean = false): Promise<{ feedback_id: number; status: string }> {
    return this.makeRequest('/feedback', {
      method: 'POST',
      body: JSON.stringify({
        student_id: studentId,
        teacher_id: teacherId,
        feedback_data: feedbackData,
        anonymous,
      }),
    });
  }

  /**
   * Generate conference preparation materials
   */
  async prepareConference(studentId: number, teacherId: number, conferenceDate: string, conferenceType: string = 'regular'): Promise<Record<string, unknown>> {
    return this.makeRequest('/conferences/prepare', {
      method: 'POST',
      body: JSON.stringify({
        student_id: studentId,
        teacher_id: teacherId,
        conference_date: conferenceDate,
        conference_type: conferenceType,
      }),
    });
  }
}

export const parentPortalApi = new ParentPortalApiService(); 